// Grader API Service
import de from 'platform/i18n/src/locales/de';
import ApiService from './ApiService';
import { buildGraderStudyIndexQueryParams } from '../utils/graderStudyIndexFilters';

class GraderService {
  constructor() {
    this.apiService = ApiService.getInstance();
  }

  /** Shared filter query params for /grader/studies and /grader/get-count (excludes pagination). */
  buildStudyFilterQueryParams(params = {}) {
    const {
      grader_id = 'all',
      client_id = 'all',
      protocol_id = 'all',
      status,
      start_date,
      end_date,
      search = '',
      submission_id,
      is_escalated,
      is_flagged,
      is_random_sampled,
      has_missing_fields,
      has_outliers,
      is_too_fast,
      is_excessive,
    } = params;

    const queryParams = {
      grader_id,
      client_id,
      protocol_id,
    };

    if (status) queryParams.status = status;
    if (start_date) queryParams.start_date = start_date;
    if (end_date) queryParams.end_date = end_date;
    if (search) queryParams.search = search;
    if (params.study_uid) queryParams.study_uid = params.study_uid;
    if (submission_id) queryParams.submission_id = submission_id;

    if (typeof is_escalated === 'boolean') {
      queryParams.is_escalated = is_escalated;
    }
    if (typeof is_flagged === 'boolean') {
      queryParams.is_flagged = is_flagged;
    }
    if (typeof is_random_sampled === 'boolean') {
      queryParams.is_random_sampled = is_random_sampled;
    }
    if (typeof has_missing_fields === 'boolean') {
      queryParams.has_missing_fields = has_missing_fields;
    }
    if (typeof has_outliers === 'boolean') {
      queryParams.has_outliers = has_outliers;
    }
    if (typeof is_too_fast === 'boolean') {
      queryParams.is_too_fast = is_too_fast;
    }
    if (typeof is_excessive === 'boolean') {
      queryParams.is_excessive = is_excessive;
    }

    return queryParams;
  }

  // Get studies for grader with pagination and filters
  async getStudies(params = {}) {
    const { page = 1, size = 10, ...filterParams } = params;

    const queryParams = {
      page,
      size,
      ...this.buildStudyFilterQueryParams(filterParams),
    };

    try {
      const baseUrl = this.apiService.getGradingServiceUrl();
      const response = await this.apiService.get('/grader/studies', queryParams, baseUrl);

      if (response.success && response.data) {
        // Parse the API response format: { is_success, status, message, data: [{studies, total, page, size}] }
        const apiData = response.data;

        if (apiData.is_success && apiData.data && apiData.data.length > 0) {
          const studiesData = apiData.data[0];
          return {
            data: studiesData.studies || [],
            total: studiesData.total_count || 0,
            page: studiesData.page || page,
            size: studiesData.size || size,
            totalPages: Math.ceil((studiesData.total_count || 0) / size),
          };
        }
      }

      // If API call fails, return empty data
      console.warn('Failed to fetch studies from API, using empty data');
      return {
        data: [],
        total: 0,
        page,
        size,
        totalPages: 0,
      };
    } catch (error) {
      console.error('Error fetching studies:', error);
      return {
        data: [],
        total: 0,
        page,
        size,
        totalPages: 0,
      };
    }
  }

  // Get status counts for studies list cards (same filters as /grader/studies)
  async getStatusCounts(params = {}) {
    const queryParams = this.buildStudyFilterQueryParams(params);

    try {
      const baseUrl = this.apiService.getGradingServiceUrl();
      const response = await this.apiService.get('/grader/get-count', queryParams, baseUrl);

      if (response.success && response.data) {
        const apiData = response.data;
        if (apiData.is_success && Array.isArray(apiData.data) && apiData.data.length > 0) {
          const counts = apiData.data[0] || {};
          return {
            PENDING: Number(counts.PENDING || 0),
            ASSIGNED: Number(counts.ASSIGNED || 0),
            IN_PROGRESS: Number(counts.IN_PROGRESS || 0),
            COMPLETED: Number(counts.COMPLETED || 0),
          };
        }
      }

      console.warn('Failed to fetch status counts from API, using zeros');
      return {
        PENDING: 0,
        ASSIGNED: 0,
        IN_PROGRESS: 0,
        COMPLETED: 0,
      };
    } catch (error) {
      console.error('Error fetching status counts:', error);
      return {
        PENDING: 0,
        ASSIGNED: 0,
        IN_PROGRESS: 0,
        COMPLETED: 0,
      };
    }
  }

  /**
   * Study position in grader queue (GET /grader/study/index?study_id=...).
   * studyId may be omitted to use sessionStorage key `id` (set when opening the viewer).
   * When opened from a submission, pass submission_id (from options or sessionStorage).
   * Response row includes current/prev/next study_id and study_uid; optional job_id,
   * prev_job_id, next_job_id for navigation without a studies-list lookup.
   */
  async getStudyIndex(studyId, options = {}) {
    const queryParams = buildGraderStudyIndexQueryParams(studyId, options);
    if (!queryParams.study_id) {
      return { success: false, error: 'Missing study_id', data: null };
    }
    try {

      const baseUrl = this.apiService.getGradingServiceUrl();
      const response = await this.apiService.get(
        '/grader/study/index',
        queryParams,
        baseUrl
      );

      if (!response.success || response.data == null) {
        return { success: false, error: response.error || 'Request failed', data: null };
      }

      const apiData = response.data;
      if (!apiData.is_success || !Array.isArray(apiData.data) || apiData.data.length === 0) {
        return {
          success: false,
          error: apiData.message || 'No index data',
          data: null,
        };
      }

      return { success: true, data: apiData.data[0] };
    } catch (error) {
      console.error('Error fetching study index:', error);
      return { success: false, error: error.message, data: null };
    }
  }

  /**
   * Resolve a studies-list row by study_uid for job_id / assignees / status.
   * Tries: dedicated study_uid param (if backend supports), search, with/without submission scope.
   */
  async findStudyRowByStudyUid(studyUid, options = {}) {
    const { graderId = 'all' } = options;
    if (!studyUid) {
      return null;
    }

    const submissionId =
      typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('currentSubmissionId') : null;

    const tryList = async extraParams => {
      const params = {
        page: 1,
        size: 500,
        grader_id: graderId,
        client_id: 'all',
        protocol_id: 'all',
        ...extraParams,
      };
      const result = await this.getStudies(params);
      const list = result.data || [];
      return list.find(s => s.study_uid === studyUid) || null;
    };

    try {
      let row = await tryList({ study_uid: studyUid });
      if (row) {
        return row;
      }

      if (submissionId) {
        row = await tryList({ study_uid: studyUid, submission_id: submissionId });
        if (row) {
          return row;
        }
      }

      row = await tryList({ search: studyUid });
      if (row) {
        return row;
      }

      if (submissionId) {
        row = await tryList({ search: studyUid, submission_id: submissionId });
        if (row) {
          return row;
        }
      }
    } catch (error) {
      console.error('Error finding study by UID:', error);
    }

    return null;
  }

  /**
   * When /grader/studies cannot resolve a row, GET /annotations/study/{studyUid} (no job_id)
   * often returns study metadata including job_id (same as viewer preload).
   */
  async resolveJobContextFromAnnotationsApi(studyUid) {
    if (!studyUid) {
      return null;
    }
    try {
      const baseUrl = this.apiService.getGradingServiceUrl();
      const endpoint = `/annotations/study/${encodeURIComponent(studyUid)}`;
      const response = await this.apiService.get(endpoint, undefined, baseUrl);

      if (!response.success || response.data == null) {
        return null;
      }

      const body = response.data;
      if (!body.is_success || !Array.isArray(body.data) || body.data.length === 0) {
        return null;
      }

      const first = body.data[0];
      const study = first.study || first.study_info || null;
      if (!study) {
        return null;
      }

      const jobId = study.job_id ?? study.jobId ?? study.jobID ?? study.id;
      if (jobId == null || jobId === '') {
        return null;
      }

      return { jobId: String(jobId), study };
    } catch (error) {
      console.warn('[GraderService] resolveJobContextFromAnnotationsApi failed:', error);
      return null;
    }
  }

  // Get dummy data for development
  getDummyStudies(params = {}) {
    const { page = 1, size = 10 } = params;

    // Generate dummy studies data
    const dummyStudies = [
      {
        id: 1,
        job_id: 101,
        patient_id: 'P001',
        study_uid: '2.25.269859997690759739055099378767846712697',
        status: 'pending',
        grader_id: 2,
        assigned_at: '2024-01-15T10:30:00Z',
        completed_at: null,
        client_id: 'ORG001',
        submission_date: '2024-01-10T09:00:00Z',
        protocol: 'Retinal grading',
        instructions:
          ' Measure choroidal thickness values, document pachyvessel presence, mark any anomalies (CC, HRF, RIFT), and provide qualitative remarks if relevant.',
        start_date: '2024-01-12T08:00:00Z',
        end_date: '2024-01-20T17:00:00Z',
        created_at: '2024-01-10T08:30:00Z',
        assigned_graders: [1, 2, 3, 4], // List of graders assigned to this job
      },
      {
        id: 2,
        job_id: 102,
        patient_id: 'P002',
        study_uid: '2.25.269859997690759739055099378767846712698',
        status: 'processing',
        grader_id: 2,
        assigned_at: '2024-01-16T14:20:00Z',
        completed_at: null,
        client_id: 'ORG001',
        submission_date: '2024-01-11T11:15:00Z',
        protocol: 'Retinal grading',
        instructions:
          ' Measure choroidal thickness values, document pachyvessel presence, mark any anomalies (CC, HRF, RIFT), and provide qualitative remarks if relevant.',
        start_date: '2024-01-13T09:00:00Z',
        end_date: '2024-01-21T18:00:00Z',
        created_at: '2024-01-11T10:45:00Z',
        assigned_graders: [2, 3, 5],
      },
      {
        id: 3,
        job_id: 103,
        patient_id: 'P003',
        study_uid: '2.25.269859997690759739055099378767846712699',
        status: 'completed',
        grader_id: 2,
        assigned_at: '2024-01-14T16:45:00Z',
        completed_at: '2024-01-18T15:30:00Z',
        client_id: 'ORG001',
        submission_date: '2024-01-09T13:20:00Z',
        protocol: 'Retinal grading',
        instructions:
          ' Measure choroidal thickness values, document pachyvessel presence, mark any anomalies (CC, HRF, RIFT), and provide qualitative remarks if relevant.',
        start_date: '2024-01-11T07:00:00Z',
        end_date: '2024-01-19T16:00:00Z',
        created_at: '2024-01-09T12:30:00Z',
        assigned_graders: [1, 2, 4, 6],
      },
      {
        id: 4,
        job_id: 104,
        patient_id: 'P004',
        study_uid: '2.25.269859997690759739055099378767846712700',
        status: 'pending',
        grader_id: 2,
        assigned_at: '2024-01-17T09:15:00Z',
        completed_at: null,
        client_id: 'ORG001',
        submission_date: '2024-01-12T15:30:00Z',
        protocol: 'Retinal grading',
        instructions:
          ' Measure choroidal thickness values, document pachyvessel presence, mark any anomalies (CC, HRF, RIFT), and provide qualitative remarks if relevant.',
        start_date: '2024-01-14T08:30:00Z',
        end_date: '2024-01-22T17:30:00Z',
        created_at: '2024-01-12T14:45:00Z',
        assigned_graders: [2, 3, 4],
      },
      {
        id: 5,
        job_id: 105,
        patient_id: 'P005',
        study_uid: '2.25.269859997690759739055099378767846712701',
        status: 'processing',
        grader_id: 2,
        assigned_at: '2024-01-18T11:00:00Z',
        completed_at: null,
        client_id: 'ORG001',
        submission_date: '2024-01-13T10:45:00Z',
        protocol: 'Retinal grading',
        instructions:
          ' Measure choroidal thickness values, document pachyvessel presence, mark any anomalies (CC, HRF, RIFT), and provide qualitative remarks if relevant.',
        start_date: '2024-01-15T09:15:00Z',
        end_date: '2024-01-23T18:15:00Z',
        created_at: '2024-01-13T09:30:00Z',
        assigned_graders: [1, 2, 5, 6],
      },
      {
        id: 6,
        job_id: 106,
        patient_id: 'P006',
        study_uid: '2.25.269859997690759739055099378767846712702',
        status: 'pending',
        grader_id: 3,
        assigned_at: '2024-01-19T10:10:00Z',
        completed_at: null,
        client_id: 'ORG001',
        submission_date: '2024-01-14T08:20:00Z',
        protocol: 'Diabetic Retinopathy Screening',
        instructions:
          ' Review fundus images for microaneurysms, hemorrhages, and exudates; grade severity using ETDRS scale and flag poor-quality images.',
        start_date: '2024-01-16T08:00:00Z',
        end_date: '2024-01-25T17:00:00Z',
        created_at: '2024-01-14T08:05:00Z',
        assigned_graders: [2, 3],
      },
      // {
      //   id: 7,
      //   job_id: 107,
      //   patient_id: 'P007',
      //   study_uid: '2.25.269859997690759739055099378767846712703',
      //   status: 'completed',
      //   grader_id: 4,
      //   assigned_at: '2024-01-12T12:40:00Z',
      //   completed_at: '2024-01-16T16:25:00Z',
      //   client_id: 'ORG001',
      //   submission_date: '2024-01-10T07:55:00Z',
      //   protocol: 'Glaucoma Assessment',
      //   instructions:
      //     ' Evaluate optic nerve head for cupping, RNFL defects, and peripapillary atrophy; add disc photos requiring follow-up.',
      //   start_date: '2024-01-13T09:30:00Z',
      //   end_date: '2024-01-16T16:00:00Z',
      //   created_at: '2024-01-10T07:40:00Z',
      //   assigned_graders: [1, 4, 5],
      // },
    ];

    // Apply pagination
    const startIndex = (page - 1) * size;
    const endIndex = startIndex + size;
    const paginatedStudies = dummyStudies.slice(startIndex, endIndex);

    return {
      data: paginatedStudies,
      total: dummyStudies.length,
      page: page,
      size: size,
      totalPages: Math.ceil(dummyStudies.length / size),
    };
  }

  // Get grader details (for dropdown)
  async getGraders() {
    try {
      const baseUrl = this.apiService.getAuthServiceUrl();
      const response = await this.apiService.get('/grader/get-users', undefined, baseUrl);

      if (response.success) {
        const rawData = Array.isArray(response.data?.data)
          ? response.data.data
          : Array.isArray(response.data)
            ? response.data
            : [];

        let list = [];
        if (Array.isArray(rawData)) {
          rawData.forEach(item => {
            if (Array.isArray(item?.users)) {
              list = list.concat(item.users);
            } else if (item) {
              list.push(item);
            }
          });
        } else if (Array.isArray(rawData?.users)) {
          list = rawData.users;
        }

        const mapped = list.map(u => ({
          id: Number(u.id ?? u.user_id ?? u.ID ?? 0),
          name: String(u.name ?? u.username ?? '').trim(),
          username: u.username || '',
          email: u.email || '',
          role: u.role || '',
          first_name: u.first_name ?? u.firstName ?? '',
          middle_name: u.middle_name ?? u.middleName ?? '',
          last_name: u.last_name ?? u.lastName ?? ''
        })).filter(u => u.id && (u.name || u.first_name || u.last_name || u.username));

        return mapped;
      }

      // Fallback to empty array if API fails
      console.warn('Failed to fetch graders from API');
      return [];
    } catch (error) {
      console.error('Error fetching graders:', error);
      return [];
    }
  }

  // Reassign grader(s) to a study. Payload always uses grader_ids array (2-eye: [id], 4-eye: [id1, id2]).
  async changeGrader(studyId, jobId, newGraderId) {
    const graderIds = Array.isArray(newGraderId) ? newGraderId : [newGraderId];
    return this.changeGraders(studyId, jobId, graderIds);
  }

  async changeGraders(studyId, jobId, graderIds) {
    try {
      const baseUrl = this.apiService.getGradingServiceUrl();
      const payload = {
        job_id: jobId,
        study_id: studyId,
        grader_ids: Array.isArray(graderIds) ? graderIds : [graderIds]
      };

      console.log('Reassigning graders with payload:', payload);

      const response = await this.apiService.post('/grader/study/assign', payload, baseUrl);

      if (response.success) {
        console.log('Graders reassigned successfully:', response.data);
        return {
          success: true,
          message: response.data?.message || 'Grader(s) assigned successfully'
        };
      }

      console.error('Failed to reassign graders:', response.error);
      return {
        success: false,
        error: response.error || 'Failed to assign graders'
      };
    } catch (error) {
      console.error('Error changing graders:', error);
      return { success: false, error: error.message };
    }
  }

  // Escalate study to administrator
  async escalateStudy(studyId, reason, category) {
    try {
      const baseUrl = this.apiService.getGradingServiceUrl();
      const payload = {
        study_id: studyId,
        reason: reason || '',
        category: category ? String(category).toUpperCase() : undefined
      };

      console.log('Escalating study with payload:', payload);

      const response = await this.apiService.post('/grader/escalate-study', payload, baseUrl);

      if (response.success && response.data) {
        const apiData = response.data;

        if (apiData.is_success) {
          console.log('Study escalated successfully:', apiData.message);
          return {
            success: true,
            message: apiData.message || 'Study escalated to administrator successfully'
          };
        }
      }

      console.error('Failed to escalate study:', response.error);
      return {
        success: false,
        error: response.error || response.data?.message || 'Failed to escalate study'
      };
    } catch (error) {
      console.error('Error escalating study:', error);
      return { success: false, error: error.message };
    }
  }

  // Get auth token (placeholder)
  getAuthToken() {
    // This should return the actual auth token
    return localStorage.getItem('authToken') || '';
  }
}

// Create singleton instance
const graderService = new GraderService();

export default graderService;
